<template>
  <Head title="Importación de datos" />
  <div>
    <div class="hidden border-yellow-200 bg-yellow-50 text-yellow-700 text-yellow-900 border-orange-200 bg-orange-50 text-orange-700 text-orange-900 border-green-200 bg-green-50 text-green-700 text-green-900 border-red-200 bg-red-50 text-red-700 text-red-900 border-gray-200 bg-gray-50 text-gray-600 text-gray-800 border-indigo-200 bg-indigo-50 text-indigo-700 text-indigo-900"></div>
    <div v-if="showReportModal && hasReport" class="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
      <div class="absolute inset-0 bg-gray-900 bg-opacity-50" @click="closeReportModal"></div>
      <div class="relative w-full max-w-4xl max-h-full overflow-hidden rounded-lg bg-white shadow-2xl">
        <div class="flex items-start justify-between px-6 py-5 border-b border-gray-200">
          <div>
            <p class="text-xs font-semibold uppercase tracking-wide" :class="reportEyebrowClass">Resultado de la importación</p>
            <h2 class="mt-1 text-2xl font-bold text-gray-900">{{ reportTitle }}</h2>
            <p v-if="reportMessage" class="mt-2 text-sm text-gray-600">{{ reportMessage }}</p>
          </div>
          <div class="flex items-start ml-4">
            <span class="px-3 py-1 mt-1 mr-3 text-xs font-semibold rounded-full" :class="reportBadgeClass">{{ reportBadgeLabel }}</span>
            <button type="button" class="p-2 text-gray-400 hover:text-gray-700" @click="closeReportModal">
              <svg class="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M14.348 5.652a.75.75 0 0 0-1.06 0L10 8.94 6.712 5.652a.75.75 0 1 0-1.06 1.06L8.94 10l-3.288 3.288a.75.75 0 1 0 1.06 1.06L10 11.06l3.288 3.288a.75.75 0 0 0 1.06-1.06L11.06 10l3.288-3.288a.75.75 0 0 0 0-1.06z" /></svg>
            </button>
          </div>
        </div>

        <div class="px-6 py-5 overflow-y-auto" style="max-height: calc(100vh - 12rem);">
          <div v-if="report.summary?.length">
            <h3 class="text-sm font-semibold text-gray-800">Resumen</h3>
            <div class="grid gap-3 mt-3 md:grid-cols-3">
              <div v-for="item in summaryItems" :key="item.line" class="px-4 py-3 rounded border text-sm" :class="item.cardClass">
                <div class="text-xs font-semibold uppercase tracking-wide" :class="item.labelClass">{{ item.label }}</div>
                <div class="mt-2 text-2xl font-bold" :class="item.valueClass">{{ item.value }}</div>
              </div>
            </div>
          </div>

          <div v-if="report.warnings?.length" class="mt-5">
            <button type="button" class="flex items-center justify-between w-full px-4 py-3 text-left rounded border border-yellow-200 bg-yellow-50" @click="showWarnings = !showWarnings">
              <span class="text-sm font-semibold text-yellow-900">Avisos ({{ report.warnings.length }})</span>
              <span class="text-xs font-semibold text-yellow-700">{{ showWarnings ? 'Ocultar' : 'Mostrar' }}</span>
            </button>
            <div v-if="showWarnings" class="mt-3 px-4 py-3 rounded border border-yellow-200 bg-white">
              <ul class="space-y-2 text-sm text-yellow-900">
                <li v-for="line in report.warnings" :key="line">{{ line }}</li>
              </ul>
            </div>
          </div>

          <div v-if="report.errors?.length" class="mt-5">
            <button type="button" class="flex items-center justify-between w-full px-4 py-3 text-left rounded border border-red-200 bg-red-50" @click="showErrors = !showErrors">
              <span class="text-sm font-semibold text-red-900">Errores ({{ report.errors.length }})</span>
              <span class="text-xs font-semibold text-red-700">{{ showErrors ? 'Ocultar' : 'Mostrar' }}</span>
            </button>
            <div v-if="showErrors" class="mt-3 px-4 py-3 rounded border border-red-100 bg-white">
              <ul class="space-y-2 text-sm text-red-900">
                <li v-for="line in report.errors" :key="line">{{ line }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="md:flex">
      <div class="w-full px-2 mb-4">
        <h2 class="mb-8 text-3xl font-bold">Importación de Oficinas</h2>
        <div class="px-8 py-4 pb-8 shadow rounded border border-gray-100 bg-white max-w-3xl">
          <form class="flex flex-col justify-between" @submit.prevent="importOrganizations">
            <file-input v-model="form.file" class="pb-8 w-full" type="file" accept="xlsx/*" label="Fichero Excel" />
            <loading-button :loading="form.processing" class="btn-orange ml-auto" type="submit">Importar</loading-button>
          </form>
        </div>
      </div>
      <div class="w-full px-2">
        <h2 class="mb-8 text-3xl font-bold">Importación de Códigos</h2>
        <div class="px-8 py-4 pb-8 shadow rounded border border-gray-100 bg-white max-w-3xl">
          <form class="flex flex-col justify-between" @submit.prevent="importCodes">
            <file-input v-model="codeForm.file" class="pb-8 w-full" type="file" accept="xlsx/*" label="Fichero Excel" />
            <loading-button :loading="codeForm.processing" class="btn-orange ml-auto" type="submit">Importar</loading-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import FileInput from '@/Shared/FileInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  components: {
    FileInput,
    Head,
    LoadingButton,
  },
  layout: Layout,
  remember: ['form', 'codeForm'],
  data() {
    return {
      form: this.$inertia.form({
        file: null,
      }),
      codeForm: this.$inertia.form({
        file: null,
      }),
      showReportModal: false,
      showWarnings: true,
      showErrors: true,
    }
  },
  mounted() {
    if (this.hasReport) {
      this.openReportModal()
    }

    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  computed: {
    report() {
      return this.$page.props.flash.report || null
    },
    hasReport() {
      return !!this.report
    },
    summaryItems() {
      return (this.report?.summary || []).map((line) => {
        const [label, ...valueParts] = line.split(':')
        const value = valueParts.join(':').trim() || '-'
        const tone = this.summaryTone(label)

        return {
          line,
          label: label.trim(),
          value,
          cardClass: tone.cardClass,
          labelClass: tone.labelClass,
          valueClass: tone.valueClass,
        }
      })
    },
    reportMessage() {
      return this.$page.props.flash.success || this.$page.props.flash.error || null
    },
    reportIsError() {
      return !!(this.$page.props.flash.error || this.report?.errors?.length)
    },
    reportTitle() {
      return this.reportIsError ? 'La importación necesita revisión' : 'Importación procesada correctamente'
    },
    reportBadgeLabel() {
      return this.reportIsError ? 'Con incidencias' : 'Correcta'
    },
    reportEyebrowClass() {
      return this.reportIsError ? 'text-red-600' : 'text-green-600'
    },
    reportBadgeClass() {
      return this.reportIsError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
    },
  },
  watch: {
    '$page.props.flash.report': {
      handler(report) {
        if (!report) {
          this.showReportModal = false
          return
        }

        this.openReportModal()
      },
      deep: true,
    },
  },
  methods: {
    summaryTone(label) {
      const normalized = label.toLowerCase()

      if (normalized.includes('errores')) {
        return {
          cardClass: 'border-red-200 bg-red-50',
          labelClass: 'text-red-700',
          valueClass: 'text-red-900',
        }
      }

      if (normalized.includes('duplicados') || normalized.includes('desactivadas') || normalized.includes('vacias')) {
        return {
          cardClass: 'border-yellow-200 bg-yellow-50',
          labelClass: 'text-yellow-700',
          valueClass: 'text-yellow-900',
        }
      }

      if (normalized.includes('actualizadas')) {
        return {
          cardClass: 'border-orange-200 bg-orange-50',
          labelClass: 'text-orange-700',
          valueClass: 'text-orange-900',
        }
      }

      if (normalized.includes('creadas') || normalized.includes('restauradas') || normalized.includes('validas')) {
        return {
          cardClass: 'border-green-200 bg-green-50',
          labelClass: 'text-green-700',
          valueClass: 'text-green-900',
        }
      }

      if (normalized.includes('sin cambios')) {
        return {
          cardClass: 'border-gray-200 bg-gray-50',
          labelClass: 'text-gray-600',
          valueClass: 'text-gray-800',
        }
      }

      return {
        cardClass: 'border-indigo-200 bg-indigo-50',
        labelClass: 'text-indigo-700',
        valueClass: 'text-indigo-900',
      }
    },
    openReportModal() {
      this.showReportModal = true
      this.showWarnings = true
      this.showErrors = true
    },
    closeReportModal() {
      this.showReportModal = false
    },
    handleKeydown(event) {
      if (event.key === 'Escape' && this.showReportModal) {
        this.closeReportModal()
      }
    },
    importOrganizations() {
      this.form.post('/oficinas/importar')
    },
    importCodes() {
      this.codeForm.post('importar-codigos')
    },
  },
}
</script>
